import { useConsentActions } from "../../hooks/use-consent-actions.js";
import { HipaaConsentRadioButtons } from "../shared/HipaaConsentRadioButtons.js";
import { ConsentDetails } from "../consent-details/ConsentDetails.js";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ConditionalComponent, Stack, StickyFooter } from "@myphx-engineering/phx-design-system";
import { useIsMobile } from "@myphx-engineering/phx-design-system/hooks";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";

//#region src/client/components/capture-consents/CaptureConsentDocument.tsx
const CaptureConsentDocument = ({ currentGroupIndex, setCurrentGroupIndex, groupedConsents, config, loading, setLoading }) => {
	const { onComplete, onError, ...consentActionConfig } = config;
	const { batchUpdateConsents } = useConsentActions(consentActionConfig);
	const { t } = useTranslation("cons");
	const isMobile = useIsMobile("md");
	const [consentDecisions, setConsentDecisions] = useState({});
	const currentDocuments = groupedConsents[currentGroupIndex]?.documents || [];
	const isHipaa = currentDocuments.length === 1 && currentDocuments[0].consentType === "hipaa";
	const checkCount = currentDocuments.filter((doc) => consentDecisions[doc.id]).length;
	const anyConsentsChecked = checkCount > 0;
	const proceedToNextOrSubmit = async (groupDecisions) => {
		const updatedDecisions = {
			...consentDecisions,
			...groupDecisions
		};
		if (currentDocuments.some((doc) => doc.consentType === "hipaa" && !groupDecisions[doc.id])) {
			const allRemainingDeclined = { ...updatedDecisions };
			for (let i = currentGroupIndex + 1; i < groupedConsents.length; i++) groupedConsents[i].documents.forEach((doc) => {
				allRemainingDeclined[doc.id] = false;
			});
			setConsentDecisions(allRemainingDeclined);
			await submitAllConsents(allRemainingDeclined);
		} else {
			setConsentDecisions(updatedDecisions);
			if (currentGroupIndex === groupedConsents.length - 1) await submitAllConsents(updatedDecisions);
			else setCurrentGroupIndex(currentGroupIndex + 1);
		}
	};
	const allowContinue = anyConsentsChecked || isHipaa && Object.keys(consentDecisions).includes(currentDocuments[0].id);
	const handleContinue = () => {
		if (!allowContinue) return;
		proceedToNextOrSubmit(Object.fromEntries(currentDocuments.map((document) => [document.id, consentDecisions[document.id] ?? false])));
	};
	const handleContinueWithoutAccepting = () => {
		proceedToNextOrSubmit(Object.fromEntries(currentDocuments.map((document) => [document.id, false])));
	};
	const submitAllConsents = async (decisions) => {
		try {
			setLoading(true);
			await batchUpdateConsents(groupedConsents.flatMap((group) => group.documents).map((consent) => ({
				consent,
				status: decisions[consent.id] ? "ACTIVE" : "DECLINED"
			})));
			onComplete?.();
		} catch (error) {
			console.error("Error updating consents:", error);
			onError?.();
		} finally {
			setLoading(false);
		}
	};
	const handleCheckboxChange = (consentId, isAttested) => {
		setConsentDecisions((prev) => ({
			...prev,
			[consentId]: isAttested
		}));
	};
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(Stack, {
		gap: "md",
		children: currentDocuments.map((consent) => /* @__PURE__ */ jsx(ConsentDetails, {
			consent,
			isAttested: consentDecisions[consent.id] || false,
			onCheckboxChange: handleCheckboxChange,
			shouldAllowConsentAttestation: !isHipaa
		}, consent.id))
	}), /* @__PURE__ */ jsx(StickyFooter, {
		active: isHipaa && isMobile,
		children: /* @__PURE__ */ jsxs(Stack, {
			gap: "lg",
			children: [
				/* @__PURE__ */ jsx(ConditionalComponent, {
					condition: isHipaa,
					children: /* @__PURE__ */ jsx(HipaaConsentRadioButtons, {
						consent: currentDocuments[0],
						isAttested: consentDecisions[currentDocuments[0].id] || false,
						onCheckboxChange: handleCheckboxChange
					})
				}),
				/* @__PURE__ */ jsx(Button, {
					onClick: handleContinue,
					disabled: !allowContinue,
					loading,
					size: "lg",
					children: currentDocuments.length > 1 ? t("consents.capture.actions.continue", { count: checkCount }) : t("consents.capture.actions.continueGroupOfOne", { count: checkCount })
				}),
				!isHipaa && /* @__PURE__ */ jsx(Button, {
					disabled: anyConsentsChecked,
					variant: "subtle",
					size: "lg",
					onClick: handleContinueWithoutAccepting,
					loading,
					children: t("consents.capture.actions.continueWithoutAccepting", { count: currentDocuments.length })
				})
			]
		})
	})] });
};

//#endregion
export { CaptureConsentDocument };